import type { ClassicInput, ProfessionalInput } from '@cls-nordic/pdf-templates/browser';

import { RCertificatePropertiesQuery, RCertificateTemplateType } from '@/store/v2';

type CertificateProperties = NonNullable<RCertificatePropertiesQuery['certificateProperties']>;

export type CertificatePreviewInput = ProfessionalInput | ClassicInput;

type SignatureInput = NonNullable<ProfessionalInput['signatures']>[number];

type CertificatePreviewInputArgs = {
  certificateProperties?: CertificateProperties;
  learnerName: string;
  journeyTitle?: string | null;
};

type LogoInput = NonNullable<ProfessionalInput['logos']>[number];

const mapSignatures = (certificateProperties: CertificateProperties): SignatureInput[] =>
  [...certificateProperties.signatures]
    .sort((a, b) => a.order - b.order)
    .map((signature) => ({
      name: signature.name ?? null,
      title: signature.title ?? null,
      institution: signature.institution ?? null,
      signatureUrl: signature.signatureUrl ?? null,
    }));

const mapLogos = (certificateProperties: CertificateProperties): LogoInput[] =>
  [...certificateProperties.logos]
    .sort((a, b) => a.order - b.order)
    .map((logo) => ({
      logoUrl: logo.logoUrl ?? null,
      organisationText: logo.organisationText ?? null,
    }));

// Classic logos are positional for the renderer: index 0 = header, index 1 = footer.
// Stored logos are keyed by order (1 = header, 2 = footer); keep the header slot (even
// when empty) whenever a footer logo exists so the footer stays at index 1.
const buildClassicLogos = (certificateProperties: CertificateProperties): LogoInput[] | undefined => {
  const header = certificateProperties.logos.find((logo) => logo.order === 1)?.logoUrl ?? null;
  const footer = certificateProperties.logos.find((logo) => logo.order === 2)?.logoUrl ?? null;

  if (footer !== null) {
    return [{ logoUrl: header }, { logoUrl: footer }];
  }

  if (header !== null) {
    return [{ logoUrl: header }];
  }

  return undefined;
};

/**
 * Maps certificate properties (plus the learner's name and journey title) to the PDF preview input
 * for the stored template. Returns null until certificate properties are available.
 */
export const certificatePropertiesToPreviewInput = ({
  certificateProperties,
  learnerName,
  journeyTitle,
}: CertificatePreviewInputArgs): CertificatePreviewInput | null => {
  if (!certificateProperties) {
    return null;
  }

  const signatures = mapSignatures(certificateProperties);
  const logos = mapLogos(certificateProperties);
  const resolvedJourneyTitle = certificateProperties.title ?? journeyTitle ?? null;
  const dates = certificateProperties.date ?? null;

  if (certificateProperties.templateType === RCertificateTemplateType.Classic) {
    return {
      learnerName,
      header: certificateProperties.header ?? null,
      journeyTitle: resolvedJourneyTitle,
      dates,
      signatures,
      logos: buildClassicLogos(certificateProperties),
    };
  }

  return {
    learnerName,
    journeyTitle: resolvedJourneyTitle,
    dates,
    description: certificateProperties.description ?? null,
    logos,
    signatures,
  };
};
